"use client";

import { RegisteredComponent } from "@builder.io/sdk-react";
import dynamic from "next/dynamic";

import { IMAGE_FILE_TYPES } from "@/libs/builder/consts";
import { withValidation } from "@/libs/builder/validators/validateBuilderComponentData";

export const callToActionInputs = [
  {
    name: "variant",
    type: "string",
    required: true,
    defaultValue: "cta",
    enum: [
      { value: "cta", label: "Call to action" },
      { value: "whatisploom", label: "What is ploom" },
      { value: "rewards", label: "Explore rewards" },
    ],
  },
  {
    name: "title",
    type: "string",
    required: true,
    defaultValue: "Join Ploom Club today!",
  },
  {
    name: "titleLoggedIn",
    friendlyName:
      "Title for logged user with {#userPoints#} placeholder for points display",
    showIf: `options.get("variant") === "rewards"`,
    type: "string",
    required: true,
    defaultValue: "You have {#userPoints#} points",
  },
  {
    name: "restrictedContent",
    friendlyName:
      "Use restricted content for logged in users who did not join Loyalty program",
    showIf: `options.get("variant") === "rewards"`,
    type: "boolean",
    defaultValue: false,
  },
  {
    name: "restrictedTitle",
    friendlyName: "Title for restricted user",
    showIf: `options.get("restrictedContent") === true`,
    type: "string",
    required: true,
    defaultValue: "Join Ploom Club",
  },
  {
    name: "restrictedDescription",
    friendlyName: "Description for restricted user",
    showIf: `options.get("restrictedContent") === true`,
    type: "string",
    required: true,
    defaultValue:
      "Join Ploom Club to get access to exclusive rewards and offers.",
  },
  {
    name: "text",
    friendlyName: "One line description",
    showIf: `options.get("variant") !== "rewards"`,
    type: "string",
  },
  {
    name: "text",
    friendlyName: "Description",
    showIf: `options.get("variant") === "rewards"`,
    type: "richText",
  },
  {
    name: "background",
    friendlyName: "Background image",
    type: "file",
    required: true,
    allowedFileTypes: IMAGE_FILE_TYPES,
    defaultValue:
      "https://cdn.builder.io/api/v1/image/assets%2F90293f7dc9704cb5935f357315020051%2F0e1d6bb8acf545c5a63ec12d1b015a41",
  },
  {
    name: "mobileBackground",
    friendlyName: "Mobile background image",
    type: "file",
    allowedFileTypes: IMAGE_FILE_TYPES,
    required: true,
    defaultValue:
      "https://cdn.builder.io/api/v1/image/assets%2F90293f7dc9704cb5935f357315020051%2Fbd97d30e2d6c4130bd97262205f5fb22",
  },
  {
    name: "description",
    friendlyName: "Ploom description",
    showIf: `options.get("variant") === "whatisploom"`,
    type: "richText",
  },
  {
    name: "theme",
    friendlyName: "Component's theme",
    showIf: `options.get("variant") === "whatisploom"`,
    type: "string",
    enum: [
      { value: "light", label: "Light" },
      { value: "dark", label: "Dark" },
    ],
    defaultValue: "light",
  },
  {
    name: "headerPosition",
    friendlyName: "Header position",
    showIf: `options.get("variant") === "whatisploom"`,
    type: "string",
    enum: ["left", "right"],
    defaultValue: "left",
  },
  {
    name: "buttonPosition",
    friendlyName: "Button position",
    showIf: `options.get("variant") === "whatisploom"`,
    type: "string",
    enum: ["left", "right"],
    defaultValue: "left",
  },
  {
    name: "buttonImage",
    friendlyName: "Button image",
    showIf: `options.get("variant") === "whatisploom"`,
    type: "file",
    allowedFileTypes: IMAGE_FILE_TYPES,
  },
  {
    name: "animatedButton",
    friendlyName: "Animated button",
    showIf: `options.get("variant") === "whatisploom"`,
    type: "boolean",
    defaultValue: false,
  },
  {
    name: "animatedLabel",
    friendlyName:
      "Animated label shwon in place of button when not hovered over component",
    showIf: `options.get("variant") === "whatisploom"`,
    type: "string",
  },
  {
    name: "animatedBackground",
    friendlyName: "Animated background will scale up on hover",
    showIf: `options.get("variant") === "whatisploom"`,
    type: "boolean",
    defaultValue: false,
  },
  {
    name: "withGradient",
    friendlyName: "Allow gradient on background",
    showIf: `options.get("variant") === "whatisploom"`,
    type: "boolean",
    defaultValue: false,
  },
  {
    name: "ctaTitle",
    friendlyName: "Label for CTA button",
    type: "string",
    required: true,
    defaultValue: "Sign in",
  },
  {
    name: "ctaUrl",
    friendlyName: "Url for CTA button",
    type: "url",
    required: true,
    defaultValue: "#",
  },
  {
    name: "secondaryCtaTitle",
    friendlyName: "Label for secondary CTA button",
    type: "string",
    defaultValue: "Join now",
  },
  {
    name: "secondaryCtaUrl",
    friendlyName: "Url for secondary CTA button",
    type: "url",
    defaultValue: "#",
  },
  {
    name: "ctaTitleLoggedIn",
    friendlyName: "Label for CTA button for logged in user",
    type: "string",
    required: true,
    showIf: `options.get("variant") === "rewards"`,
    defaultValue: "Explore rewards",
  },
  {
    name: "ctaUrlLoggedIn",
    friendlyName: "Url for CTA button for logged in user",
    type: "string",
    required: true,
    showIf: `options.get("variant") === "rewards"`,
    defaultValue: "#",
  },
  {
    name: "secondaryCtaTitleLoggedIn",
    friendlyName: "Label for secondary CTA button for logged in user",
    type: "string",
    showIf: `options.get("variant") === "rewards"`,
    defaultValue: "Points history",
  },
  {
    name: "secondaryCtaUrlLoggedIn",
    friendlyName: "Url for secondary CTA button for logged in user",
    type: "url",
    showIf: `options.get("variant") === "rewards"`,
    defaultValue: "#",
  },
  {
    name: "ctaRestrictedTitle",
    friendlyName: "Label for CTA button for logged in but restricted user",
    type: "string",
    required: true,
    showIf: `options.get("restrictedContent") === true`,
    defaultValue: "Profile",
  },
  {
    name: "ctaRestrictedUrl",
    friendlyName: "Url for CTA button for logged in but restricted user",
    type: "url",
    required: true,
    showIf: `options.get("restrictedContent") === true`,
    defaultValue: "#",
  },
  {
    name: "secondaryCtaRestrictedTitle",
    friendlyName:
      "Label for secondary CTA button for logged in but restricted user",
    type: "string",
    showIf: `options.get("restrictedContent") === true`,
    defaultValue: "Home",
  },
  {
    name: "secondaryCtaRestrictedUrl",
    friendlyName:
      "Url for secondary CTA button for logged in but restricted user",
    type: "url",
    showIf: `options.get("restrictedContent") === true`,
    defaultValue: "#",
  },
] as const satisfies Inputs;

export const CallToActionComponent: RegisteredComponent = {
  name: "CallToAction",
  component: dynamic(async () =>
    withValidation(
      callToActionInputs,
      (await import("./CallToAction")).CallToAction,
      "CallToAction"
    )
  ),
  inputs: callToActionInputs,
};
